import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { tokenService } from "../tokenService";

type CreateReauthOptions = {
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;
};

/**
 * creates a base query wrapper with automatic token refresh on 401 responses.
 * 
 * features:
 * - proactive refresh: checks token expiry before making requests
 * - reactive refresh: automatically refreshes on 401 errors (silent)
 * - token rotation: supports updating both access and refresh tokens
 * - mutex-based: prevents multiple simultaneous refresh attempts
 * - silent refresh: no notifications or redirects during refresh
 * 
 * @param baseQuery - the base query function to wrap
 * @returns enhanced base query with automatic reauth
 */
export const createBaseQueryWithReauth = ({
  baseQuery,
}: CreateReauthOptions): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  return async (args, api, extraOptions) => {
    // proactive refresh: try to refresh token before request if it's expiring soon
    await tokenService.getValidAccessToken();

    // make the request
    let result = await baseQuery(args, api, extraOptions);

    // reactive refresh: if we get 401, refresh and retry
    if (result.error && result.error.status === 401) {
      console.log("[BaseQuery] Got 401, attempting token refresh...");
      
      // use silent refresh to avoid showing notifications
      const tokens = await tokenService.refreshSilent();
      
      if (tokens) {
        // retry the original request with new token
        console.log("[BaseQuery] Retrying request with new token...");
        result = await baseQuery(args, api, extraOptions);
      } else {
        console.log("[BaseQuery] Token refresh failed, request will fail");
        // tokens are already cleared by tokenService
      }
    }

    return result;
  };
};
